import { privateKeyToAccount, type Address, type Account } from "viem/accounts";

import type { HTTPURL, RuntimeMode, ValidChainId } from "@types";

import type { ContractFunctionExecutionError } from "viem";

export const isContractFunctionExecutionError = (
	error: unknown,
): error is ContractFunctionExecutionError => {
	if(typeof error === "object" && !Array.isArray(error) && error !== null) {
		if("name" in error) {
			return error.name === "ContractFunctionExecutionError";
		}		
	}
	return false;
};

export const raise = (message: string) => {
	throw new Error(message);
};

export const pause = async (timeout = 250) => {
	return new Promise((res) => setTimeout(() => res(true), timeout));
};

export declare type GetRPCURLProps = {
	chainId?: ValidChainId;
	mode?: RuntimeMode;
	url?: HTTPURL;
};

export const getRPCURL = ({
	chainId = 1,
	mode = "PROD",
	url,
}: GetRPCURLProps): HTTPURL => {
	if (url) return url;
	if (`CHAIN_${chainId}_RPC_${mode}` in process.env) {
		return process.env[`CHAIN_${chainId}_RPC_${mode}`] as HTTPURL;
	}
	if (`CHAIN_${chainId}_RPC` in process.env) {
		return process.env[`CHAIN_${chainId}_RPC`] as HTTPURL;
	}
	throw new Error(`environment has no RPC configured for chain ${chainId}`);
};

export const getConfiguredAccount = (key?: Address): Account | undefined => {

	if(key) {
		return privateKeyToAccount(key);
	}

	const { PRIVATE_WALLET_KEY, CHAIN_1_PK, } = process.env;
	if(PRIVATE_WALLET_KEY) {
		return privateKeyToAccount(PRIVATE_WALLET_KEY as `0x${string}`);
	}
	if(CHAIN_1_PK) {
		return privateKeyToAccount(CHAIN_1_PK as `0x${string}`);
	}

	throw new Error(
		"getConfiguredAccount invoked without PRIVATE_WALLET_KEY present in environment",
	);
};

export declare type ParsedContractFunctionExecutionError = {
	type: string;
	message: string;
};

export const parseContractError = (err: unknown ) : undefined | ParsedContractFunctionExecutionError => {
	if(isContractFunctionExecutionError(err)) {
		return {
			type: err.cause.data.errorName,
			message: err.message,
		}
	}
};
export declare type LogProps = {
	call: string;
	contract: string;
	payload: unknown;
	result?: unknown;
};

/**
 * keep this async! in case we add unified logging features.
 * @param param0
 */
export const logcall = async ({
	call,
	contract,
	payload,
	result,
}: LogProps): Promise<void> => {
	const logs = [
		`contract: ${contract}`,
		`call: ${call}`,
		"params: \n",
		stringifysafe(Object.assign(payload, { abi: undefined })),
		"\n",
		// typeof result !== "undefined" ? `result: ${result.toString()}` : "no result provided",
	]
		.filter((v) => v)
		.join("\n");
	console.log(logs);
};

export const stringifysafe = (value) => {
	const result = JSON.stringify(
		value,
		(key, value) => {
			if (typeof value === "bigint") {
				return value.toString();
			}
			return value;
		},
		1,
	);
	return result;
};

export const runtime = (): "NODE" | "BROWSER" => {
	return typeof process !== "undefined" &&
		process.versions &&
		process.versions.node
		? "NODE"
		: "BROWSER";
};
